import { ButtonDestructive, ButtonPrimary, ButtonStatus, ButtonTertiary } from "@nova/component-btn"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { InputOutlined } from "@nova/ds-component-input"
import { HTMLAttributes, useCallback, useEffect, useRef, useState } from "react"
import classNames from "classnames"
import { usePilotTrackingConnectedAccountMutation } from "../usePilotTrackingConnectedAccountMutation"
import {
    ConfirmationModalProps,
    useConfirmationModalContext,
} from "features/modals/WorkspaceConfirmationModalV2"
import * as Icons from "@nova/component-icons"
import { ConnectedAccountDeleteSkydioModal } from "../delete/ConnectedAccountDeleteSkydioModal"
import { IntegrationType, NonSensitiveFlightRecordConnectedAccount } from "@nova/schemas"

export const ConnectedAccountEditSkydioModal = ({
    confirm,
    cancel,
    isPerformingAsyncConfirmCallback: _,
    isPerformingAsyncCancelCallback: __,
    account,
    onSave,
    onDelete,
    ...rest
}: ConfirmationModalProps & {
    account: NonSensitiveFlightRecordConnectedAccount<IntegrationType.skydio>
    onSave: (id: string, token: string) => Promise<void>
    onDelete: () => Promise<void>
} & HTMLAttributes<HTMLDivElement>) => {
    const [token, setToken] = useState("")
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        inputRef.current?.focus()
    }, [])

    const { trigger, isLoading } = usePilotTrackingConnectedAccountMutation({
        key: "skydio-connection",
        confirm,
        cancel,
        mutate: useCallback(async () => {
            await onSave(account.id, token)
        }, [onSave, account.id, token]),
    })

    const confirmationModal = useConfirmationModalContext()
    const showDeleteModal = async () => {
        await confirmationModal.confirm({
            RenderComponent: (props) => (
                <ConnectedAccountDeleteSkydioModal
                    {...props}
                    account={account}
                    onDelete={onDelete}
                />
            ),
        })
    }

    return (
        <HighPriorityNovaModalContainer
            {...rest}
            title="Edit Account"
            closeModal={cancel}
            className={classNames("w-[28rem]", rest.className)}
            bottomBar={
                <div className="flex w-full gap-2">
                    <ButtonDestructive
                        title="Disconnect"
                        LeftIcon={Icons.LinkOff}
                        className="mr-auto"
                        onClick={() => showDeleteModal()}
                    />
                    <ButtonTertiary title="Cancel" onClick={cancel} />
                    <ButtonPrimary
                        title="Save"
                        onClick={() => trigger()}
                        status={isLoading ? ButtonStatus.Loading : ButtonStatus.Normal}
                        disabled={isLoading}
                    />
                </div>
            }
        >
            <form
                className="flex flex-col gap-4 mt-4"
                onSubmit={(e) => {
                    e.preventDefault()
                    trigger()
                }}
            >
                <InputOutlined
                    ref={inputRef}
                    placeholder="API Token"
                    label="Skydio API Token"
                    autoComplete="off"
                    type="text"
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                />

                <button type="submit" className="hidden" aria-hidden="true" />
            </form>
        </HighPriorityNovaModalContainer>
    )
}
